import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Target, CheckCircle, Sparkles, TrendingUp, Shield, Lightbulb, Crown, Users, Quote, Calendar, Clock } from 'lucide-react';
import { rolesVisionnairesPRIsME, synergieTOP3 } from '../data/gouvernanceComiteData';
import { formatLastAnalysisDate } from '../utils/dateFormatter';
import { useLexLastSync } from '../contexts/LexLastSyncContext';

interface LeadershipPRIsMEModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LeadershipPRIsMEModal({ isOpen, onClose }: LeadershipPRIsMEModalProps) {
  const [activeLeadersTab, setActiveLeadersTab] = useState<'leaders' | 'impressions' | 'checkpoints'>('leaders');
  const { dates: lexDates } = useLexLastSync();
  const lastAnalysisDate = isOpen ? (lexDates['prisme_leadership_last_ai_analysis'] ?? null) : null;

  const leaders = [
    { name: 'Dany Bourassa', role: 'Vision & Produit', avatar: '/DB.png', note: 'Fondateur PRIsME • Vision directe, action, excellence.', impression: 'PRIsME est la promesse tenue: valeur tangible, vitesse, rigueur. On fonce.', isTop3: true, departement: 'Top 3' },
    { name: 'François Delisle', role: 'VP Tech & Architecture', avatar: '/FP.png', note: 'Précision, structure, qualité et scalabilité.', impression: 'Architecture claire, qualité mesurable: PRIsME peut scaler proprement.', isTop3: true, departement: 'Top 3' },
    { name: 'Dany Bourassa', role: 'CA/CE & Croissance', avatar: '/DB.png', note: 'Vision affaires, positionnement, go-to-market.', impression: 'Narratif commercial puissant; on tient un différenciateur marché.', isTop3: true, departement: 'Top 3' },
    // Service & Direction TI
    { name: 'Jean-Pierre Leclerc', role: 'Directeur Service TI', avatar: '/JP.png', note: 'Performance, chiffres, objectifs. PRIsME élève la qualité.', impression: 'Objectifs, chiffres, livrables: PRIsME va booster la performance.', isTop3: false, departement: 'Service & Direction TI' },
    { name: 'Eric Hains', role: 'DA Service TI & Actionnaire', avatar: '/EH.png', note: 'Machine d\'exécution, volume, relations humaines naturelles. Premier implanteur PRIsME client.', impression: 'Le concept est en place: je passe en mode exécution et volume.', isTop3: false, departement: 'Service & Direction TI' },
    { name: 'Vincent Bourassa', role: 'Leader technique et sécurité • Directeur Adjoint', avatar: '/VB.png', note: 'Pilier technique, équilibre total… sauf la comm 😉.', impression: 'Solide techniquement; je veille à la sécurité et à la stabilité.', isTop3: false, departement: 'Service & Direction TI' },
    { name: 'Jean-Michel Caouette', role: 'DA Service TI • Prime d\'exception BZ', avatar: '/JMC.png', note: 'Prime d\'exception, écoute client, autonomie, service exemplaire.', impression: 'Proche du client, PRIsME me donne des outils concrets, j\'exécute.', isTop3: false, departement: 'Service & Direction TI' },
    // Ventes & Marketing
    { name: 'Antoine Giroux', role: 'Directeur des ventes', avatar: '/AG-transparent.png', note: 'Natural et grounder sur les résultats. Mobilise et motive son équipe vers l\'excellence.', impression: 'PRIsME structure notre approche commerciale et garantit la qualité. Différenciateur commercial.', isTop3: false, departement: 'Ventes & Marketing' },
    { name: 'Rosalie Emond', role: 'Responsable marketing', avatar: '/RE.png', note: 'Communicatrice née, naturelle en public. Énergie et don de raconteuse exceptionnels.', impression: 'PRIsME renforce notre présence événementielle et notre communication. Transforme nos événements en expériences mémorables.', isTop3: false, departement: 'Ventes & Marketing' },
    // Programmation & IA
    { name: 'Eric Durand', role: 'Directeur de la programmation', avatar: '/ED.png', note: 'Travailleur acharné, jugement et sens de l\'analyse remarquables. Direct au but, fit naturel avec BZ.', impression: 'PRIsME structure notre excellence en programmation. Approche directe et efficace.', isTop3: false, departement: 'Programmation & IA' },
    // Infonuagique
    { name: 'Richard Delisle', role: 'Directeur Infonuagique - Protecteur du Cloud', avatar: '/RD.png', note: 'Rigueur exemplaire, fiabilité absolue (bougon adoré 😉). Tout est loggué, contrôlé, méthodique.', impression: 'Si c\'est documenté et reproductible, je le supporte. On exige la rigueur.', isTop3: false, departement: 'Infonuagique' },
    // Cybersécurité
    { name: 'Francis Marquis', role: 'Directeur Solutions d\'affaires & Cybersécurité', avatar: '/FM.png', note: 'Ancien militaire – protocole, professionnalisme, excellence continue. Calme exemplaire, prestance rassurante.', impression: 'Cadre sécuritaire solide en devenir; discipline opérationnelle à maintenir. Le cœur de BZ.', isTop3: false, departement: 'Cybersécurité' },
    // Administration & RH
    { name: 'Mélissa Pelletier', role: 'Directrice de l\'administration', avatar: '/MP.png', note: 'Femme d\'exception, valeurs et loyauté incroyables. Rien ne lui échappe, adore l\'humain et les chiffres.', impression: 'PRIsME structure notre excellence administrative. Personne de confiance, bonté hors du commun.', isTop3: false, departement: 'Administration & RH' },
    { name: 'Émilie Quintal', role: 'Directrice des recevables et Chef des opérations admin', avatar: '/EQ.png', note: 'Travaillante née, abat l\'ouvrage comme pas une. Protège les dollars avec constance et jugement.', impression: 'PRIsME structure notre rigueur financière. Protection des ressources chèrement gagnées par l\'équipe.', isTop3: false, departement: 'Administration & RH' },
  ];

  const Avatar = ({ src, name }: { src?: string; name: string }) => (
    <div className="w-14 h-14 rounded-full bg-gray-200 overflow-hidden flex items-center justify-center text-sm font-bold text-gray-600">
      {src ? (
        <img src={src} alt={name} className="w-full h-full object-cover" />
      ) : (
        <span>{name.split(' ').map((n) => n[0]).join('').slice(0, 2)}</span>
      )}
    </div>
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Overlay */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3 }}
          className="relative w-[95vw] h-[90vh] bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="relative bg-gradient-to-r from-purple-600 via-orange-600 to-pink-600 p-8">
            <div className="absolute inset-0 bg-grid-white/10 pointer-events-none" />
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-10 p-2 bg-white/20 hover:bg-white/30 rounded-xl transition-colors backdrop-blur-sm cursor-pointer"
            >
              <X className="w-6 h-6 text-white" />
            </button>

            <div className="relative flex items-center gap-6">
              <div className="bg-white/20 backdrop-blur-xl p-5 rounded-2xl">
                <Crown className="w-12 h-12 text-white" />
              </div>
              <div>
                <h1 className="text-4xl font-black text-white mb-2">Leadership PRIsME</h1>
                <p className="text-purple-100 text-lg font-semibold">Le Top 3 Visionnaires et les Leaders qui transforment l'industrie TI</p>
                {lastAnalysisDate && (
                  <div className="flex items-center gap-2 mt-2 text-xs text-purple-200">
                    <Clock className="w-3.5 h-3.5" />
                    <span>Dernière analyse AI pour LEX: {formatLastAnalysisDate(lastAnalysisDate)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Content Area - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            {/* SECTION TOP 3 VISIONNAIRES */}
            <div className="p-8 bg-gradient-to-br from-purple-50 via-orange-50 to-pink-50">
              <div className="max-w-7xl mx-auto">
                {/* Titre section Top 3 */}
                <div className="text-center mb-8">
                  <div className="inline-block bg-gradient-to-r from-purple-600 via-pink-600 to-orange-600 p-4 rounded-2xl mb-4">
                    <Crown className="w-16 h-16 text-white" />
                  </div>
                  <h2 className="text-4xl font-black text-gray-900 mb-3">
                    Le Top 3 Leadership PRIsME
                  </h2>
                  <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                    La trinité qui propulse PRIsME vers l'excellence
                  </p>
                </div>

                {/* Cards des 3 leaders */}
                <div className="grid grid-cols-1 gap-8 mb-8">
                  {rolesVisionnairesPRIsME.map((leader) => {
                    // Mapping des avatars pour le Top 3
                    const avatarMap: { [key: string]: string } = {
                      'Dany Bourassa': '/DB.png',
                      'François Delisle': '/FP.png'
                    };
                    const avatarSrc = avatarMap[leader.nom] || '';
                    
                    return (
                      <div key={leader.id} className={`bg-gradient-to-br ${leader.couleurGradient} p-8 rounded-3xl text-white shadow-2xl`}>
                        <div className="flex items-start gap-6 mb-6">
                          <div className="bg-white/20 backdrop-blur-xl p-5 rounded-2xl flex-shrink-0">
                            {avatarSrc ? (
                              <img 
                                src={avatarSrc} 
                                alt={leader.nom}
                                className="w-20 h-20 rounded-full object-cover border-4 border-white/30 shadow-lg"
                              />
                            ) : (
                              <div className="w-20 h-20 rounded-full bg-white/30 flex items-center justify-center text-white text-2xl font-bold">
                                {leader.nom.split(' ').map(n => n[0]).join('')}
                              </div>
                            )}
                          </div>
                          <div className="flex-1">
                            <h3 className="text-3xl font-black mb-2">{leader.nom}</h3>
                            <p className="text-xl font-semibold opacity-90 mb-1">{leader.titre}</p>
                            <p className="text-lg opacity-80">{leader.rolePRIsME}</p>
                          </div>
                        </div>

                        {/* Vision */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
                          <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                            <Target className="w-6 h-6" />
                            Vision
                          </h4>
                          <p className="text-lg leading-relaxed">{leader.vision}</p>
                        </div>

                        {/* Responsabilités */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
                          <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <CheckCircle className="w-6 h-6" />
                            Responsabilités dans PRIsME
                          </h4>
                          <div className="grid grid-cols-1 gap-3">
                            {leader.responsabilites.map((resp, idx) => (
                              <div key={idx} className="flex items-start gap-3">
                                <div className="bg-white/20 rounded-full p-1 mt-1">
                                  <CheckCircle className="w-4 h-4" />
                                </div>
                                <span className="text-base">{resp}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Impact */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
                          <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                            <Sparkles className="w-6 h-6" />
                            Impact
                          </h4>
                          <p className="text-lg font-semibold leading-relaxed">{leader.impact}</p>
                        </div>

                        {/* Compétences Clés */}
                        <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-6">
                          <h4 className="text-xl font-bold mb-4 flex items-center gap-2">
                            <Crown className="w-6 h-6" />
                            Compétences Clés
                          </h4>
                          <div className="flex flex-wrap gap-3">
                            {leader.competences.map((comp, idx) => (
                              <div key={idx} className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full font-semibold text-sm">
                                {comp}
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Philosophie */}
                        <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-6 border-2 border-white/30">
                          <h4 className="text-xl font-bold mb-3 flex items-center gap-2">
                            <Lightbulb className="w-6 h-6" />
                            Philosophie
                          </h4>
                          <p className="text-lg italic leading-relaxed">"{leader.philosophie}"</p>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Section Synergie */}
                <div className="bg-gradient-to-r from-gray-900 via-purple-900 to-gray-900 p-8 rounded-3xl text-white">
                  <h2 className="text-3xl font-black mb-3 text-center flex items-center justify-center gap-3">
                    <Sparkles className="w-8 h-8" />
                    {synergieTOP3.titre}
                    <Sparkles className="w-8 h-8" />
                  </h2>
                  <p className="text-xl text-center mb-8 text-purple-200">{synergieTOP3.description}</p>

                  <div className="grid grid-cols-1 gap-6 mb-8">
                    {synergieTOP3.synergies.map((synergie, idx) => (
                      <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border-2 border-purple-400/30">
                        <h3 className="text-2xl font-bold mb-3 text-purple-300">{synergie.lien}</h3>
                        <p className="text-lg mb-3">{synergie.description}</p>
                        <div className="bg-green-500/20 border-2 border-green-400/50 rounded-xl p-4">
                          <p className="text-base font-semibold text-green-200">
                            <span className="text-green-400">→ Résultat:</span> {synergie.resultat}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Conclusion finale */}
                  <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-8 rounded-2xl text-center border-4 border-white/20">
                    <h3 className="text-2xl font-black mb-4 flex items-center justify-center gap-2">
                      <Crown className="w-8 h-8" />
                      La Formule Gagnante
                    </h3>
                    <p className="text-2xl font-bold leading-relaxed">
                      {synergieTOP3.conclusion}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* SÉPARATEUR VISUEL */}
            <div className="relative py-12 bg-white">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center">
                <div className="bg-white px-8 py-4 rounded-full border-2 border-bz-blue shadow-lg">
                  <div className="flex items-center gap-3">
                    <Users className="w-6 h-6 text-bz-blue" />
                    <h3 className="text-2xl font-black text-bz-dark">Les Leaders PRIsME</h3>
                  </div>
                  <p className="text-sm text-gray-600 text-center mt-2">Sans eux, PRIsME n'est qu'une idée. Avec eux, c'est un mouvement vivant.</p>
                </div>
              </div>
            </div>

            {/* SECTION LEADERS PRIsME PAR DÉPARTEMENT */}
            <div className="p-8 bg-gradient-to-br from-blue-50 to-indigo-50">
              <div className="max-w-7xl mx-auto">
                {/* Message Dany */}
                <div className="bg-gradient-to-r from-bz-blue/10 to-blue-50 border-l-4 border-bz-blue p-5 rounded-r-xl mb-6">
                  <p className="text-gray-800"><strong>Message Dany:</strong> « PRIsME n'existe que si mes leaders le portent sur le terrain. Vous êtes la VOIX, la PRÉSENCE, l'ÉNERGIE. On écrit l'histoire ensemble. »</p>
                </div>

                {/* Leaders par Département */}
                <div className="space-y-8 mb-8">
                  {/* Service & Direction TI */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-blue-200">
                    <h3 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                      <Shield className="w-7 h-7 text-blue-600" />
                      Service & Direction TI
                      <span className="ml-auto bg-blue-500 text-white text-sm font-bold px-3 py-1 rounded-full">4 Leaders</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border rounded-xl p-4 bg-blue-50/50">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar src="/JP.png" name="Jean-Pierre Leclerc" />
                          <div>
                            <p className="font-bold text-gray-900">Jean-Pierre Leclerc</p>
                            <p className="text-sm text-gray-600">Directeur du département</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">Vision: Excellence opérationnelle et satisfaction client. PRIsME structure notre expertise et la rend accessible.</p>
                      </div>
                      <div className="border rounded-xl p-4 bg-blue-50/50">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar src="/EH.png" name="Eric Hains" />
                          <div>
                            <p className="font-bold text-gray-900">Eric Hains</p>
                            <p className="text-sm text-gray-600">Directeur Adjoint • Premier implanteur PRIsME client</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">Vision: PRIsME transforme notre façon de livrer de la valeur. Machine d'exécution et volume.</p>
                      </div>
                      <div className="border rounded-xl p-4 bg-blue-50/50">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar src="/VB.png" name="Vincent Bourassa" />
                          <div>
                            <p className="font-bold text-gray-900">Vincent Bourassa</p>
                            <p className="text-sm text-gray-600">Leader technique et sécurité • Directeur Adjoint</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">Vision: PRIsME assure la cohérence technique et la qualité dans tous nos livrables.</p>
                      </div>
                      <div className="border rounded-xl p-4 bg-blue-50/50">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar src="/JMC.png" name="Jean-Michel Caouette" />
                          <div>
                            <p className="font-bold text-gray-900">Jean-Michel Caouette</p>
                            <p className="text-sm text-gray-600">Directeur Adjoint • Prime d'exception BZ</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">Vision: PRIsME permet de créer des livrables tangibles parfaits pour chaque client.</p>
                      </div>
                    </div>
                  </div>

                  {/* Ventes & Marketing */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-orange-200">
                    <h3 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                      <TrendingUp className="w-7 h-7 text-orange-600" />
                      Ventes & Marketing
                      <span className="ml-auto bg-orange-500 text-white text-sm font-bold px-3 py-1 rounded-full">2 Leaders</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border rounded-xl p-4 bg-orange-50/50">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar src="/AG-transparent.png" name="Antoine Giroux" />
                          <div>
                            <p className="font-bold text-gray-900">Antoine Giroux</p>
                            <p className="text-sm text-gray-600">Directeur des ventes</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">Vision: PRIsME structure notre approche commerciale et garantit la qualité de nos livrables. Différenciateur commercial.</p>
                      </div>
                      <div className="border rounded-xl p-4 bg-orange-50/50">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar src="/RE.png" name="Rosalie Emond" />
                          <div>
                            <p className="font-bold text-gray-900">Rosalie Emond</p>
                            <p className="text-sm text-gray-600">Responsable marketing</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">Vision: PRIsME structure notre communication et notre présence événementielle. Communicateur né.</p>
                      </div>
                    </div>
                  </div>

                  {/* Programmation & IA */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-200">
                    <h3 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                      <Sparkles className="w-7 h-7 text-purple-600" />
                      Programmation & IA
                      <span className="ml-auto bg-purple-500 text-white text-sm font-bold px-3 py-1 rounded-full">1 Leader</span>
                    </h3>
                    <div className="border rounded-xl p-4 bg-purple-50/50">
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar src="/ED.png" name="Eric Durand" />
                        <div>
                          <p className="font-bold text-gray-900">Eric Durand</p>
                          <p className="text-sm text-gray-600">Directeur de la programmation</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">Vision: PRIsME structure notre excellence en programmation. Travailleur acharné, jugement et sens de l'analyse remarquables.</p>
                    </div>
                  </div>

                  {/* Infonuagique */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-cyan-200">
                    <h3 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                      <Shield className="w-7 h-7 text-cyan-600" />
                      Infonuagique
                      <span className="ml-auto bg-cyan-500 text-white text-sm font-bold px-3 py-1 rounded-full">1 Leader</span>
                    </h3>
                    <div className="border rounded-xl p-4 bg-cyan-50/50">
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar src="/RD.png" name="Richard Delisle" />
                        <div>
                          <p className="font-bold text-gray-900">Richard Delisle</p>
                          <p className="text-sm text-gray-600">Directeur Infonuagique - Protecteur du Cloud</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">Vision: PRIsME structure et documente notre excellence cloud. Rigueur mémorable, dévouement exemplaire. Tout est loggué, contrôlé, méthodique.</p>
                    </div>
                  </div>

                  {/* Cybersécurité */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-red-200">
                    <h3 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                      <Shield className="w-7 h-7 text-red-600" />
                      Cybersécurité
                      <span className="ml-auto bg-red-500 text-white text-sm font-bold px-3 py-1 rounded-full">1 Leader</span>
                    </h3>
                    <div className="border rounded-xl p-4 bg-red-50/50">
                      <div className="flex items-center gap-3 mb-2">
                        <Avatar src="/FM.png" name="Francis Marquis" />
                        <div>
                          <p className="font-bold text-gray-900">Francis Marquis</p>
                          <p className="text-sm text-gray-600">Directeur Cybersécurité & Solutions d'affaires</p>
                        </div>
                      </div>
                      <p className="text-sm text-gray-700">Vision: PRIsME structure notre excellence cybersécurité et rassure nos clients. Calme exemplaire, professionnalisme indéniable. Le cœur de BZ.</p>
                    </div>
                  </div>

                  {/* Administration & RH */}
                  <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-pink-200">
                    <h3 className="text-2xl font-black text-gray-900 mb-4 flex items-center gap-3">
                      <Users className="w-7 h-7 text-pink-600" />
                      Administration & RH
                      <span className="ml-auto bg-pink-500 text-white text-sm font-bold px-3 py-1 rounded-full">2 Leaders</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="border rounded-xl p-4 bg-pink-50/50">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar src="/MP.png" name="Mélissa Pelletier" />
                          <div>
                            <p className="font-bold text-gray-900">Mélissa Pelletier</p>
                            <p className="text-sm text-gray-600">Directrice de l'administration</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">Vision: PRIsME structure notre excellence administrative. Femme d'exception, valeurs et loyauté incroyables. Rien ne lui échappe.</p>
                      </div>
                      <div className="border rounded-xl p-4 bg-pink-50/50">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar src="/EQ.png" name="Émilie Quintal" />
                          <div>
                            <p className="font-bold text-gray-900">Émilie Quintal</p>
                            <p className="text-sm text-gray-600">Directrice des recevables et Chef des opérations admin</p>
                          </div>
                        </div>
                        <p className="text-sm text-gray-700">Vision: PRIsME structure notre rigueur financière. Travaillante née, protège les dollars chèrement gagnés avec constance et jugement.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Aperçu rapide des Premières Impressions - Toujours visible */}
                <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border-2 border-yellow-200 rounded-xl p-6 mb-6 shadow-md">
                  <div className="flex items-center gap-3 mb-4">
                    <Quote className="w-6 h-6 text-yellow-600" />
                    <h3 className="text-xl font-bold text-gray-900">Premières Impressions - 30 octobre 2025</h3>
                    <span className="ml-auto bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full">9 Leaders</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-4">
                    <strong>Photo honnête du moment :</strong> Chaque leader consigne ici sa première impression. Cette trace vivante évoluera avec le temps et sera réévaluée aux checkpoints 6, 12 et 24 mois.
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {leaders.slice(0, 3).map((l) => (
                      <div key={l.name} className="bg-white rounded-lg p-3 border border-yellow-300 shadow-sm">
                        <div className="flex items-center gap-2 mb-2">
                          <Avatar src={l.avatar} name={l.name} />
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-sm text-bz-dark truncate">{l.name}</p>
                            {l.isTop3 && (
                              <span className="bg-gradient-to-r from-orange-500 to-pink-600 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">TOP 3</span>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-2 italic">"{l.impression}"</p>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-gray-600 mt-4 italic">💡 Cliquez sur l'onglet "Premières impressions" ci-dessous pour voir toutes les impressions des 9 leaders.</p>
                </div>

                {/* Aperçu rapide des Checkpoints - Toujours visible */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 mb-6 shadow-md">
                  <div className="flex items-center gap-3 mb-4">
                    <Calendar className="w-6 h-6 text-green-600" />
                    <h3 className="text-xl font-bold text-gray-900">Checkpoints PRIsME - Vision à long terme</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 border-2 border-green-300 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                        <p className="font-bold text-bz-dark">Dans 6 mois</p>
                      </div>
                      <p className="text-sm text-gray-700">Adoption réelle chez 5 clients, premiers cas d'étude, feedbacks leaders consignés.</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-2 border-green-300 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <p className="font-bold text-bz-dark">Dans 12 mois</p>
                      </div>
                      <p className="text-sm text-gray-700">Standardisation PRIsME v2, indicateurs de valeur par leader, montée en compétence certifiée.</p>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-2 border-green-300 shadow-sm">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                        <p className="font-bold text-bz-dark">Dans 24 mois</p>
                      </div>
                      <p className="text-sm text-gray-700">Références majeures, PRIsME comme différenciateur marché, leaders reconnus publiquement.</p>
                    </div>
                  </div>
                  <p className="text-xs text-gray-600 mt-4 italic">💡 Cliquez sur l'onglet "Checkpoints" ci-dessous pour voir les détails complets et les métriques à mesurer.</p>
                </div>

                {/* Tabs - Plus visibles avec badges */}
                <div className="flex border-b-2 border-gray-200 mb-6 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg overflow-hidden shadow-sm">
                  <button 
                    onClick={() => setActiveLeadersTab('leaders')} 
                    className={`px-6 py-4 font-bold flex items-center gap-2 transition-all ${
                      activeLeadersTab === 'leaders' 
                        ? 'text-bz-dark border-b-4 border-bz-blue bg-white shadow-md' 
                        : 'text-gray-600 hover:text-bz-dark hover:bg-white/50'
                    }`}
                  >
                    <Users className="w-5 h-5" /> Présentation
                  </button>
                  <button 
                    onClick={() => setActiveLeadersTab('impressions')} 
                    className={`px-6 py-4 font-bold flex items-center gap-2 transition-all relative ${
                      activeLeadersTab === 'impressions' 
                        ? 'text-bz-dark border-b-4 border-yellow-500 bg-white shadow-md' 
                        : 'text-gray-600 hover:text-bz-dark hover:bg-white/50'
                    }`}
                  >
                    <Quote className="w-5 h-5" /> Premières impressions
                    <span className="ml-2 bg-yellow-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">30 oct 2025</span>
                  </button>
                  <button 
                    onClick={() => setActiveLeadersTab('checkpoints')} 
                    className={`px-6 py-4 font-bold flex items-center gap-2 transition-all ${
                      activeLeadersTab === 'checkpoints' 
                        ? 'text-bz-dark border-b-4 border-green-500 bg-white shadow-md' 
                        : 'text-gray-600 hover:text-bz-dark hover:bg-white/50'
                    }`}
                  >
                    <Calendar className="w-5 h-5" /> Checkpoints
                    <span className="ml-2 bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">6m • 12m • 24m</span>
                  </button>
                </div>

                {/* Content selon onglet */}
                {activeLeadersTab === 'leaders' && (
                  <div className="space-y-6">
                    {/* Tous les Leaders avec Avatars - Organisés par Département */}
                    <div>
                      <h3 className="text-xl font-black text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="w-6 h-6 text-bz-blue" />
                        Tous les Leaders PRIsME ({leaders.length} leaders)
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {leaders.map((l) => (
                          <div 
                            key={l.name} 
                            className={`border-2 rounded-xl p-5 hover:shadow-xl transition-all hover:scale-105 ${
                              l.isTop3 
                                ? 'border-yellow-400 bg-gradient-to-br from-yellow-50 to-orange-50' 
                                : 'border-gray-200 bg-white hover:border-bz-blue'
                            }`}
                          >
                            <div className="flex items-start gap-4 mb-3">
                              <div className="flex-shrink-0">
                                <Avatar src={l.avatar} name={l.name} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1 flex-wrap">
                                  <p className="font-bold text-bz-dark text-base">{l.name}</p>
                                  {l.isTop3 && (
                                    <span className="bg-gradient-to-r from-orange-500 to-pink-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                      TOP 3
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-gray-600 mb-2">{l.role}</p>
                                {l.departement && !l.isTop3 && (
                                  <span className="inline-block bg-blue-100 text-blue-700 text-xs font-semibold px-2 py-0.5 rounded-full mb-2">
                                    {l.departement}
                                  </span>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed">{l.note}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {activeLeadersTab === 'impressions' && (
                  <div className="space-y-6">
                    <div className="bg-yellow-50 border-l-4 border-yellow-500 p-5 rounded-r-xl">
                      <p className="text-gray-800"><strong>30 octobre 2025 – Premières impressions par leader:</strong> on fige une photo honnête, individuelle, qui évoluera avec le temps. C'est la trace vivante de notre histoire PRIsME.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      {leaders.map((l) => (
                        <div 
                          key={l.name} 
                          className={`border rounded-xl p-4 hover:shadow-lg transition ${
                            l.isTop3 ? 'border-2 border-yellow-400 bg-yellow-50/50' : ''
                          }`}
                        >
                          <div className="flex items-center gap-3 mb-3">
                            <Avatar src={l.avatar} name={l.name} />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p className="font-bold text-bz-dark">{l.name}</p>
                                {l.isTop3 && (
                                  <span className="bg-gradient-to-r from-orange-500 to-pink-600 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                    TOP 3
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-500">{l.role}</p>
                            </div>
                          </div>
                          <div className="bg-white border rounded-lg p-3">
                            <p className="text-xs uppercase tracking-wide text-gray-500 mb-1">Première impression – 30 oct 2025</p>
                            <p className="text-sm text-gray-800">{l.impression}</p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="bg-gray-50 border rounded-xl p-5">
                      <p className="text-sm text-gray-700">
                        Explication: chaque leader consigne ici sa <strong>première impression</strong>. Dans 6, 12 et 24 mois (onglet Checkpoints), on reviendra individuellement valider l'évolution de la vision, de l'adoption et de l'impact. 
                        Cette carte devient un <strong>journal vivant</strong> de l'implantation PRIsME porté par vos leaders.
                      </p>
                    </div>
                  </div>
                )}

                {activeLeadersTab === 'checkpoints' && (
                  <div className="space-y-6">
                    <div className="bg-green-50 border-l-4 border-green-500 p-5 rounded-r-xl">
                      <p className="text-gray-800">On <strong>écrit l'histoire à l'avance</strong>. Trois checkpoints où on validera: impact, adhésion, excellence.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                      <div className="border rounded-xl p-4 bg-white">
                        <p className="font-bold text-bz-dark mb-1">Dans 6 mois</p>
                        <p className="text-sm text-gray-700">Adoption réelle chez 5 clients, premiers cas d'étude, feedbacks leaders consignés.</p>
                      </div>
                      <div className="border rounded-xl p-4 bg-white">
                        <p className="font-bold text-bz-dark mb-1">Dans 12 mois</p>
                        <p className="text-sm text-gray-700">Standardisation PRIsME v2, indicateurs de valeur par leader, montée en compétence certifiée.</p>
                      </div>
                      <div className="border rounded-xl p-4 bg-white">
                        <p className="font-bold text-bz-dark mb-1">Dans 24 mois</p>
                        <p className="text-sm text-gray-700">Références majeures, PRIsME comme différenciateur marché, leaders reconnus publiquement.</p>
                      </div>
                    </div>

                    <div className="bg-white border rounded-xl p-5">
                      <p className="font-semibold text-bz-dark mb-2 flex items-center gap-2">
                        <Target className="w-5 h-5 text-bz-blue" /> Ce qu'on va MESURER
                      </p>
                      <ul className="text-sm text-gray-700 space-y-2 list-disc pl-5">
                        <li>Clients touchés par leader et par trimestre</li>
                        <li>Livrables PRIsME produits et adoptés</li>
                        <li>Satisfaction client et renouvellements</li>
                        <li>Montée en compétences (Certification PRIsME)</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
